import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import Loader from 'components/Loader';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo, useState } from 'react';
import { useLazyGetProductivityByEmployeeAndCaseReportQuery } from 'store/api/reportingPerformance';
import { ClassifierData } from 'types/classifiers';
import { ProductivityByEmployeeAndCaseRow, ProductivityByEmployeeAndCaseTotals } from 'types/reporting/performance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

import {
    ReportCaseLink,
    ReportContactLink,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    ToneRule,
    GREEN,
    YELLOW,
    RED,
} from '../reportCells';
import {
    CASE_STATUS_ORDER,
    caseFilter,
    clientFilter,
    employeeFilter,
    inStatusOrder,
    optionsFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    searchSelectors,
    mainFilterDefaults,
} from '../reportFilters';
import { inBaseCurrency, rowCurrencies, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = ProductivityByEmployeeAndCaseRow;
type Totals = ProductivityByEmployeeAndCaseTotals;
type Figure = Exclude<keyof Totals, 'baseCurrency'>;

const REPORT_KEY = 'productivity-by-employee-and-case';
const USED_CLASSIFIERS = ['MATTER_STATE_TYPE'];
/** The report starts on the cases in progress, as the dashboard it replaces did. */
const DEFAULT_CASE_STATES = 'caseState=in=(IN_PROGRESS)';

function buildFilterConfig(classifiers: Record<string, ClassifierData>): FilterConfig {
    const filters: MainFilter[] = [
        periodFilter('reporting.filter.timecard-date-range'),
        optionsFilter(
            'caseState',
            'reporting.filter.case-status',
            inStatusOrder(getFilterOptionsFromClassifier(classifiers.MATTER_STATE_TYPE), CASE_STATUS_ORDER)
        ),
        clientFilter(),
        caseFilter(),
        employeeFilter('employeeId', 'reporting.filter.employee'),
        employeeFilter('supervisingPartnerId', 'reporting.filter.supervising-partner'),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-month' }),
        quickFilters: quickFiltersFrom(filters, { caseState: 'reporting.filter.case-status' }),
        searchBarSelectors: searchSelectors('caseNumber', 'caseName', 'clientName', 'employeeName'),
        filters,
    };
}

function figureColumn(
    id: Figure | 'hourlyRate' | 'invoicedHourlyRate',
    header: string,
    rows: Row[] | undefined,
    rule: ToneRule
): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header,
        meta: {
            ...TONED_COLUMN_META,
            widthWeight: 0.7,
            minPx: 120,
            textAlign: 'right',
            nullHandling: 'nullsLast',
            renderTotal:
                id === 'hourlyRate' || id === 'invoicedHourlyRate'
                    ? undefined
                    : (totals) => <ReportNumberCell value={totals?.[id]} />,
        },
        cell: tonedCell(rows, (row) => row[id], rule),
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        {
            id: 'supervisingPartner',
            accessorKey: 'supervisingPartner',
            header: t('reporting.column.supervising-partner'),
            meta: { widthWeight: 0.9, minPx: 140 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.supervisingPartnerId}>
                    {row.original.supervisingPartner}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'employeeName',
            accessorKey: 'employeeName',
            header: t('reporting.column.employee'),
            meta: { widthWeight: 0.9, minPx: 140 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.employeeId}>
                    {row.original.employeeName}
                </ReportEmployeeLink>
            ),
        },
        figureColumn('hourlyRate', t('reporting.column.hourly-rate'), rows, YELLOW),
        figureColumn('invoicedHourlyRate', t('reporting.column.invoiced-hourly-rate'), rows, GREEN),
        {
            id: 'caseCurrency',
            accessorKey: 'caseCurrency',
            header: t('reporting.column.case-currency'),
            meta: {
                fixedPx: 130,
                renderTotal: () => <EllipsisTooltip>{rowCurrencies(rows, (row) => row.caseCurrency)}</EllipsisTooltip>,
            },
        },
        figureColumn('nonBillableHours', t('reporting.column.non-billable-hours'), rows, RED),
        figureColumn('billableHours', t('reporting.column.billable-hours'), rows, YELLOW),
        figureColumn('invoicedHours', t('reporting.column.invoiced-hours'), rows, GREEN),
        figureColumn('billableAmount', inBaseCurrency(t('reporting.column.billable-amount'), currency), rows, YELLOW),
        figureColumn('invoicedAmount', inBaseCurrency(t('reporting.column.invoiced-amount'), currency), rows, GREEN),
    ];
}

export function ProductivityByEmployeeAndCaseReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetProductivityByEmployeeAndCaseReportQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const [initialSearchParams] = useState(() => mainFilterDefaults([DEFAULT_CASE_STATES]));
    const filterConfig = useMemo(() => buildFilterConfig(classifiers), [classifiers]);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            {isLoadingClassifiers ? (
                <Loader />
            ) : (
                <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                    data={data}
                    belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                    isLoading={isFetching}
                    isError={isError}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    initialSearchParams={initialSearchParams}
                    columns={columns}
                    columnConfigId={`report-${REPORT_KEY}`}
                    enableColumnPicker
                    enableColumnReorder
                    enableColumnResize
                    pluralTextId="reporting.rows"
                    singularTextId="reporting.row"
                    noEntriesId="reporting.no-rows"
                    noEntriesBodyId="reporting.no-rows-body"
                />
            )}
        </ReportLayout>
    );
}
